import { AssignedUserPermissions, bsConstants, getPermissions, HasIndex, UserDto } from 'booksprout'

export class PermissionService {
  public user!: UserDto
  public permissions!: AssignedUserPermissions

  public get isOnPaidPlan () {
    return this.user &&
      this.user.authorRole &&
      this.user.authorRole !== bsConstants.USER.ROLES.AUTHOR.FREE
  }

  get isDemoFeature () {
    return ['sandbox'].includes(process.env.BUILD_MODE + '')
      && ![
        'me@allangaunt.dev',
        'author.ginnie@gmail.com',
        'tomasjakelis@gmail.com'
      ].includes(this.user?.emailAddress || '')
  }

  public get isPublisher () {
    return this.roleName === bsConstants.USER.ROLES.AUTHOR.PUBLISHER
  }

  public get roleName (): string | undefined {
    if (!this.user) return ''
    // console.log(this.user.authorRole, this.user.reviewerRole)
    return this.user.authorRole ? this.user.authorRole : this.user.reviewerRole
  }

  public configure (user: UserDto | undefined, force?: boolean): PermissionService {
    // Just a precaution in case it's gets configured more than once.
    // No point doing more work - just return the instance.
    if (this.permissions !== void 0 && !force) {
      return this
    }

    if (user !== void 0) {
      this.user = user
      this.permissions = getPermissions(this.user.reviewerRole, this.user.authorRole)
    }

    return this
  }

  public create (resource: string, roleType: number): boolean {
    return this.can('create', resource, roleType)
  }

  public read (resource: string, roleType: number): boolean {
    return this.can('read', resource, roleType)
  }

  public update (resource: string, roleType: number): boolean {
    return this.can('update', resource, roleType)
  }

  public delete (resource: string, roleType: number): boolean {
    return this.can('delete', resource, roleType)
  }

  /**
   * Does a user have permission to do something
   * This code purposely only checks permissions against one of the roles. The reason being is they might have read permissions
   * for ARC on the reviewer side but not on the author side so we need to tell it which permission we're checking for when
   * calling the method.
   * @param action - The permission key we're checking the user has
   * @param resource - The value we're expecting it to have for to be true
   * @param roleType - The role of user (admin, author, reviewer etc)
   */
  public can (action: string, resource: string, roleType: number): boolean {
    if (!this.user) return false

    if (this.user.isAdmin) return true

    if (roleType === bsConstants.USER.ROLES.TYPE.AUTHOR) {
      // Permission doesn't exist in the reviewer ones, try in the author (if they exist)
      if (this.permissions.authorPerms === void 0) return false

      return (this.permissions.authorPerms as HasIndex)[action].includes(resource)
    } else if (roleType === bsConstants.USER.ROLES.TYPE.REVIEWER) {
      const result = (this.permissions.reviewerPerms as HasIndex)[action]
      if (result) return result.includes(resource)
    } else if (roleType === bsConstants.USER.ROLES.TYPE.ADMIN) {
      const result = (this.permissions.authorPerms as HasIndex)[action]
      if (result) return result.includes(resource)
    }

    return false
  }

  /**
   * The effective numeric limit for a quota action (e.g. 'maxRunningArcs'), where -1 means unlimited
   * and 0 means "no allowance". Centralises limit resolution — including the legacy-bestselling
   * exemption — so every caller (withinLimit, the date picker's maxAllowedArcs) agrees on the cap.
   * @param action - The permission key we're resolving the limit for
   */
  public effectiveLimit (action: string): number {
    if (action === 'maxRunningArcs' && this.user?.authorRole === bsConstants.USER.ROLES.AUTHOR.PUBLISHER) {
      return this.publisherMaxRunningArcs()
    }

    // Legacy bestselling users signed up before the 10-ARC cap was introduced retain unlimited campaigns
    if (action === 'maxRunningArcs' && this.user?.legacyBestsellingArcs && this.user?.authorRole === bsConstants.USER.ROLES.AUTHOR.BEST_SELLING) {
      return -1
    }

    const reviewerActionLimit = (this.permissions.reviewerPerms as HasIndex)[action]
    if (reviewerActionLimit) return reviewerActionLimit

    if (this.permissions.authorPerms === void 0) return 0

    return (this.permissions.authorPerms as HasIndex)[action] || 0
  }

  /**
   * The Publisher plan is sold as unlimited, but a new account starts on a limited number of live
   * campaigns until staff lift it [sc-29038]. Staff set the account's own number (or -1, unlimited)
   * in maxRunningArcsOverride; the accounts that were already on the plan were given -1 when the cap
   * arrived. A grandfathered bestseller who upgrades starts on the same limit as everyone else
   * [sc-29307]: staff's "Plan default" has to mean one number on every account, and keeping them
   * unlimited made it silently do nothing on theirs.
   *
   * The override only means anything on this plan, so an account that drops to a lower plan is held
   * to that plan's cap, and gets its own number back if it returns.
   */
  private publisherMaxRunningArcs (): number {
    const override = this.user.maxRunningArcsOverride
    if (override !== null && override !== void 0) {
      return override
    }

    return bsConstants.SUBSCRIPTIONS.PLANS.PUBLISHER.INITIAL_MAX_RUNNING_ARCS
  }

  /**
   * Is a user within a limit defined by their permissions?
   * @param action - The permission key we're checking the user has
   * @param currentCount - The current count of this key they have i.e current ArcCount
   */
  public withinLimit (action: string, currentCount: number): boolean {
    const limit = this.effectiveLimit(action)
    return limit === -1 || currentCount < limit
  }

  /**
   * Permissions work in a tiered fashion. This allows us to check if a user role is "good enough" to see a required role.
   * @param authorRole
   * @param reviewerRole
   */
  public hasTieredPermission (authorRole?: string, reviewerRole?: string ): boolean {
    // console.log('Has tiered: ', authorRole, reviewerRole)
    if (authorRole) {
      const authorTiers = {
        [bsConstants.USER.ROLES.AUTHOR.FREE]: [
          bsConstants.USER.ROLES.AUTHOR.FREE
        ],
        [bsConstants.USER.ROLES.AUTHOR.GROWING]: [
          bsConstants.USER.ROLES.AUTHOR.FREE,
          bsConstants.USER.ROLES.AUTHOR.GROWING
        ],
        [bsConstants.USER.ROLES.AUTHOR.PRO]: [
          bsConstants.USER.ROLES.AUTHOR.FREE,
          bsConstants.USER.ROLES.AUTHOR.GROWING,
          bsConstants.USER.ROLES.AUTHOR.PRO
        ],
        [bsConstants.USER.ROLES.AUTHOR.BEST_SELLING]: [
          bsConstants.USER.ROLES.AUTHOR.FREE,
          bsConstants.USER.ROLES.AUTHOR.GROWING,
          bsConstants.USER.ROLES.AUTHOR.PRO,
          bsConstants.USER.ROLES.AUTHOR.BEST_SELLING
        ],
        [bsConstants.USER.ROLES.AUTHOR.PUBLISHER]: [
          bsConstants.USER.ROLES.AUTHOR.FREE,
          bsConstants.USER.ROLES.AUTHOR.GROWING,
          bsConstants.USER.ROLES.AUTHOR.PRO,
          bsConstants.USER.ROLES.AUTHOR.BEST_SELLING,
          bsConstants.USER.ROLES.AUTHOR.PUBLISHER
        ]
      }
      const roles = authorTiers[this.user.authorRole || '']
      if (roles === void 0) {
        return false
      }

      return roles.includes(authorRole)
    } else if (reviewerRole) {
      const reviewerTiers = {
        [bsConstants.USER.ROLES.REVIEWER.NEW]: [
          bsConstants.USER.ROLES.REVIEWER.NEW
        ],
        [bsConstants.USER.ROLES.REVIEWER.VALUED]: [
          bsConstants.USER.ROLES.REVIEWER.NEW,
          bsConstants.USER.ROLES.REVIEWER.VALUED
        ],
        [bsConstants.USER.ROLES.REVIEWER.TOP]: [
          bsConstants.USER.ROLES.REVIEWER.NEW,
          bsConstants.USER.ROLES.REVIEWER.VALUED,
          bsConstants.USER.ROLES.REVIEWER.TOP
        ]
      }
      const roles = reviewerTiers[this.user.reviewerRole || '']
      if (roles === void 0) {
        return false
      }

      return roles.includes(reviewerRole)
    }

    return false
  }
}
